import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import AdminLayout from "./components/AdminLayout";
import { AdminCountersProvider } from "./context/AdminCountersContext";
import { ADMIN_SECTIONS, type AdminSectionKey } from "./config/sections";
import DashboardOverview from "./components/DashboardOverview";
import Analytics from "./components/Analytics";
import ShareInsights from "./components/ShareInsights";
import ApprovalQueue from "./components/ApprovalQueue";
import ReviewsModeration from "./components/ReviewsModeration";
import LeadsModeration from "./components/LeadsModeration";
import BusinessesSection from "./sections/BusinessesSection";
import FeaturedSection from "./sections/FeaturedSection";
import TaxonomySection from "./sections/TaxonomySection";
import DetailedCategoriesSection from "./sections/DetailedCategoriesSection";
import CategoryAliasesSection from "./sections/CategoryAliasesSection";
import LocationsSection from "./sections/LocationsSection";
import TouristSpotsSection from "./sections/TouristSpotsSection";
import UsersSection from "./sections/UsersSection";
import CrmPipelineSection from "./sections/CrmPipelineSection";
import ContactsSection from "./sections/ContactsSection";
import OrdersSection from "./sections/OrdersSection";
import InvoicesSection from "./sections/InvoicesSection";
import FinanceSection from "./sections/FinanceSection";
import AdsSection from "./sections/AdsSection";
import SeoSection from "./sections/SeoSection";
import MediaSection from "./sections/MediaSection";
import SubscriptionsSection from "./sections/SubscriptionsSection";
import PlansSection from "./sections/PlansSection";
import SettingsSection from "./sections/SettingsSection";

function SectionContent({ section }: { section: AdminSectionKey }) {
  switch (section) {
    case "dashboard":     return <DashboardOverview />;
    case "analytics":     return <div className="space-y-8"><Analytics /><ShareInsights /></div>;
    case "businesses":    return <BusinessesSection />;
    case "approvals":     return <ApprovalQueue />;
    case "featured":      return <FeaturedSection />;
    case "categories":    return <TaxonomySection kind="category" />;
    case "subcategories": return <TaxonomySection kind="subcategory" />;
    case "detailed-categories": return <DetailedCategoriesSection />;
    case "category-aliases":  return <CategoryAliasesSection />;
    case "locations":     return <LocationsSection />;
    case "tourist-spots": return <TouristSpotsSection />;
    case "users":         return <UsersSection />;
    case "owners":        return <UsersSection ownerOnly />;
    case "crm-pipeline":  return <CrmPipelineSection />;
    case "crm-contacts":  return <ContactsSection />;
    case "leads":         return <LeadsModeration />;
    case "orders":        return <OrdersSection />;
    case "invoices":      return <InvoicesSection />;
    case "finance":       return <FinanceSection />;
    case "reviews":       return <ReviewsModeration />;
    case "ads":           return <AdsSection kind="ads" />;
    case "ad-placements": return <AdsSection kind="placements" />;
    case "subscriptions": return <SubscriptionsSection />;
    case "plans":         return <PlansSection />;
    case "seo":           return <SeoSection />;
    case "media":         return <MediaSection />;
    case "settings":      return <SettingsSection />;
    default:              return <DashboardOverview />;
  }
}

export default function AdminPanelPage() {
  const { user, isDemoMode } = useAuth();
  const [section, setSection] = useState<AdminSectionKey>("dashboard");

  const active = ADMIN_SECTIONS.find((s) => s.key === section)!;

  return (
    <AdminCountersProvider>
      <AdminLayout active={section} onSelect={setSection}>
        <div className="mx-auto max-w-[1400px]">
          {/* Section header */}
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-100">
              <i className={`${active.icon} text-xl text-primary-700`} />
            </span>
            <div>
              <h2 className="font-heading text-2xl font-bold text-foreground-950">{active.label}</h2>
              <p className="text-sm text-foreground-500">{active.desc}</p>
            </div>
            <span className="ml-auto hidden text-xs text-foreground-500 md:block">
              Signed in as {user?.email}
              {isDemoMode && " · demo data"}
            </span>
          </div>

          <SectionContent section={section} />
        </div>
      </AdminLayout>
    </AdminCountersProvider>
  );
}